import React, { useContext, useEffect } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import {
  PersonAdd as PersonAddIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import { DataContext } from "../../../contexts/DataProvider";
import { useAlert } from "../../../functions/useAlert";
import { BORDER_RADIUS_SM } from "../../../theme/theme";
import ConflictModal from "../ConflictModal";
import { ContactModalProps } from "./types";
import { useContactForm } from "./useContactForm";
import { useAllClients } from "./useAllClients";
import { useClientLink } from "./useClientLink";
import { useContactSave } from "./useContactSave";
import ContactBasicInfoFields from "./ContactBasicInfoFields";
import ContactRelationshipSection from "./ContactRelationshipSection";
import ClientLinkSection from "./ClientLinkSection";
import ContactAdminSection from "./ContactAdminSection";
import ContactKycSection from "./ContactKycSection";
import PrimaryConfirmDialog from "./PrimaryConfirmDialog";

const ContactModal: React.FC<ContactModalProps> = ({
  open,
  onClose,
  onSave,
  contactData,
  relationshipData,
  allLinkedContacts = [],
  entityType,
}) => {
  const { login } = useContext(DataContext);
  const theme = useTheme();
  const [setAlert] = useAlert();

  const isEditMode = !!contactData;
  const hasRelationship = !!relationshipData?.relationshipId;

  const {
    formData,
    relationshipForm,
    setRelationshipForm,
    handleChange,
    showAdditionalDetails,
    setShowAdditionalDetails,
  } = useContactForm(contactData, relationshipData, open);

  const { allClients, loadingClients, loadAllClients, resetClients } = useAllClients();
  const { clientLink, setClientLink, resetClientLink } = useClientLink();

  const {
    isSaving,
    conflictModalOpen,
    duplicateContact,
    clearConflict,
    primaryConfirmOpen,
    existingPrimaryContact,
    handleSave,
    handleMerge,
    handleCreateNew,
    handlePrimaryConfirm,
    handlePrimaryCancel,
    resetSaveState,
  } = useContactSave({
    contactData,
    relationshipData,
    allLinkedContacts,
    isEditMode,
    hasRelationship,
    formData,
    relationshipForm,
    setRelationshipForm,
    clientLink,
    login,
    setAlert,
    onSave,
    onClose,
  });

  // Reset transient cross-hook state every time the dialog opens
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (open) {
      resetSaveState();
      resetClientLink();
      resetClients();
    }
  }, [contactData, relationshipData, open]);

  const newContactName =
    `${formData.firstName.trim()} ${formData.surname.trim()}`.trim();

  const boxStyle = {
    bgcolor: alpha(theme.palette.primary.main, 0.04),
    p: 2.5,
    borderRadius: BORDER_RADIUS_SM,
    mb: 2,
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={false}
        PaperProps={{
          sx: { borderRadius: "12px", width: "100%", maxWidth: "600px" },
        }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: "1.25rem", fontWeight: 700 }}>
          {isEditMode ? (
            <EditIcon color="primary" />
          ) : (
            <PersonAddIcon color="primary" />
          )}
          {isEditMode ? " Edit Contact" : " Create New Contact"}
        </DialogTitle>

        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <ContactBasicInfoFields formData={formData} onChange={handleChange} />

            {/* Relationship section — only shown when editing a linked contact */}
            {hasRelationship && (
              <Grid item xs={12}>
                <ContactRelationshipSection
                  entityType={entityType}
                  relationshipType={relationshipForm.relationshipType}
                  isPrimary={relationshipForm.isPrimary}
                  onRelationshipTypeChange={(value) =>
                    setRelationshipForm({ ...relationshipForm, relationshipType: value })
                  }
                  onPrimaryChange={(checked) =>
                    setRelationshipForm({ ...relationshipForm, isPrimary: checked })
                  }
                />
              </Grid>
            )}

            {/* Link to Client Entity (create mode only) */}
            {!isEditMode && (
              <Grid item xs={12}>
                <ClientLinkSection
                  clientLink={clientLink}
                  setClientLink={setClientLink}
                  allClients={allClients}
                  loadingClients={loadingClients}
                  onLoadClients={loadAllClients}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                onClick={() => setShowAdditionalDetails(!showAdditionalDetails)}
                sx={{
                  cursor: "pointer",
                  py: 1,
                  mt: 1,
                  userSelect: "none",
                }}
              >
                <Typography variant="subtitle2" fontWeight="bold" color="primary.main">
                  {showAdditionalDetails ? "Hide KYC & Admin Details" : "Show KYC & Admin Details"}
                </Typography>
                <IconButton size="small" color="primary">
                  {showAdditionalDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>

              <Collapse in={showAdditionalDetails} sx={{ mt: 2 }}>
                <Box sx={boxStyle}>
                  <ContactAdminSection
                    title={formData.admin.title}
                    jobTitle={formData.admin.jobTitle}
                    onTitleChange={(value) => handleChange("admin", "title", value)}
                    onJobTitleChange={(value) => handleChange("admin", "jobTitle", value)}
                  />
                </Box>

                <Box sx={{ ...boxStyle, mb: 0 }}>
                  <ContactKycSection
                    kyc={formData.kyc}
                    onChange={(field, value) => handleChange("kyc", field, value)}
                  />
                </Box>
              </Collapse>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button onClick={onClose} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={isSaving}>
            {isSaving ? (
              <CircularProgress size={24} />
            ) : isEditMode ? (
              "Update Contact"
            ) : (
              "Save Contact"
            )}
          </Button>
        </DialogActions>

        <ConflictModal
          open={conflictModalOpen}
          onClose={clearConflict}
          onMerge={handleMerge}
          onCreateNew={handleCreateNew}
          existingContact={duplicateContact || {}}
          incomingContact={{
            name: `${formData.firstName.trim()} ${formData.surname.trim()}`.trim(),
            email: formData.email,
            phone: formData.phone,
          }}
        />
      </Dialog>

      <PrimaryConfirmDialog
        open={primaryConfirmOpen}
        onCancel={handlePrimaryCancel}
        onConfirm={handlePrimaryConfirm}
        isSaving={isSaving}
        existingPrimaryContactName={existingPrimaryContact?.name}
        newContactName={newContactName}
      />
    </>
  );
};

export default ContactModal;
